import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FiAlertTriangle, FiArrowLeft, FiCheckCircle, FiKey, FiLoader } from "react-icons/fi";
import Button from "components/Button";
import Logo from "components/common/Logo";
import FormPasswordInput from "components/form/FormPasswordInput";
import AuthSplitLayout from "layouts/AuthSplitLayout";
import { api } from "lib/api";
import swtAlert from "lib/sweetAlert";
import {
  validateForm as runFormValidation,
  validatePassword,
  validateConfirmPassword,
} from "lib/validation";
import { isSuccessResponse } from "routes/auth";
import { apiURl } from "store/actions";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [doneMessage, setDoneMessage] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (errors.form) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.form;
        return next;
      });
    }
  };

  const validateAll = () => {
    const newErrors = runFormValidation(formData, {
      password: (v) => validatePassword(v),
      confirmPassword: (v) => validateConfirmPassword(v, formData.password),
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) return;
    if (!validateAll()) return;

    setIsLoading(true);
    try {
      const resp = await api.post(
        apiURl.resetPassword,
        {
          token,
          password: formData.password,
          confirmPassword: formData.confirmPassword,
        },
        { auth: false }
      );

      if (!isSuccessResponse(resp)) {
        const msg = resp?.message || "Couldn't reset password";
        setErrors((prev) => ({ ...prev, form: msg }));
        swtAlert.error("Password Reset Failed", msg);
        return;
      }

      const successMsg = resp?.message || "Password has been reset successfully.";
      setDoneMessage(successMsg);
      setIsDone(true);
      swtAlert.success("Password Reset Complete", successMsg);
    } catch (err) {
      const msg = err?.message || "Couldn't reset password";
      setErrors((prev) => ({ ...prev, form: msg }));
      swtAlert.error("Password Reset Failed", msg);
    } finally {
      setIsLoading(false);
    }
  };

  let body;

  if (!token) {
    body = <MissingTokenPanel />;
  } else if (isDone) {
    body = (
      <ResetSuccessPanel
        message={doneMessage}
        onContinue={() => navigate("/login", { replace: true })}
      />
    );
  } else {
    body = (
      <>
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Reset your password</h1>
          <p className="mt-2 text-sm text-slate-500">
            Choose a new password. Use at least 8 characters with upper, lower, number, and symbol.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <FormPasswordInput
            label="New password"
            name="password"
            autoComplete="new-password"
            value={formData.password}
            onChange={handleInputChange}
            inputClassName="bg-slate-100"
            error={errors.password}
            disabled={isLoading}
          />
          {!errors.password ? (
            <p className="text-xs text-slate-500">
              Min 8 characters with upper, lower, number and special character.
            </p>
          ) : null}

          <FormPasswordInput
            label="Confirm password"
            name="confirmPassword"
            autoComplete="new-password"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            inputClassName="bg-slate-100"
            error={errors.confirmPassword}
            disabled={isLoading}
          />

          {errors.form ? (
            <p className="text-sm font-medium text-red-600" role="alert">
              {errors.form}
            </p>
          ) : null}

          <Button
            type="submit"
            className="flex w-full items-center justify-center gap-2 py-3"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <FiLoader className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                Resetting...
              </>
            ) : (
              <>
                <FiKey className="h-4 w-4 shrink-0" aria-hidden />
                Reset password
              </>
            )}
          </Button>
        </form>
      </>
    );
  }

  return (
    <AuthSplitLayout
      heroTitle="Secure your account"
      heroDescription="Set a strong password to protect backoffice access."
    >
      <div className="w-full max-w-sm space-y-6">
        <Logo showText text="Fortify" logoClassName="h-7 w-7" textClassName="text-base font-bold text-slate-900" />
        {body}
        <Link
          to="/login"
          className="inline-flex w-full items-center justify-center gap-2 text-sm font-medium text-slate-600 underline decoration-slate-600"
        >
          <FiArrowLeft className="h-4 w-4" aria-hidden />
          Back to login
        </Link>
      </div>
    </AuthSplitLayout>
  );
}

function ResetSuccessPanel({ message, onContinue }) {
  return (
    <div className="space-y-4 rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-center text-sm text-emerald-900">
      <FiCheckCircle className="mx-auto h-10 w-10 text-emerald-600" aria-hidden />
      <h2 className="text-base font-semibold">Password reset complete</h2>
      <p>{message}</p>
      <Button type="button" className="w-full py-3" onClick={onContinue}>
        Continue to login
      </Button>
    </div>
  );
}

function MissingTokenPanel() {
  return (
    <div className="space-y-4 rounded-xl border border-amber-200 bg-amber-50 p-5 text-center text-sm text-amber-950">
      <FiAlertTriangle className="mx-auto h-10 w-10 text-amber-600" aria-hidden />
      <h2 className="text-base font-semibold">Reset link missing</h2>
      <p>
        This page needs a reset token in the URL. Open the link from your password reset email, or request a new one.
      </p>
      <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-center">
        <Link
          to="/forgot-password"
          className="inline-flex w-full items-center justify-center rounded-lg bg-brand-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-800 sm:w-auto"
        >
          Request new link
        </Link>
        <Link
          to="/login"
          className="inline-flex w-full items-center justify-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 sm:w-auto"
        >
          Back to login
        </Link>
      </div>
    </div>
  );
}
